import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, useLocation, useOutletContext } from 'react-router-dom';
import QRCode from 'react-qr-code';
import toast from 'react-hot-toast';
import {
  MessageCircle, Receipt, X, Link2, Smartphone, Copy, Clock,
  Wallet, RefreshCw, AlertTriangle, Pencil, FileText, QrCode, AlertCircle, SlidersHorizontal, Trash2,
  MoreVertical, RotateCcw, CheckCircle, ChevronDown, ImagePlus, Film,
} from 'lucide-react';
import { agentAppAPI, complaintAPI, userAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { pickTemplate, fillTemplate } from '../../utils/messageTemplates';

const fmt = (n) => new Intl.NumberFormat('en-IN').format(Math.round(n || 0));

// Fallback while the admin-defined list loads (mirrors backend defaults in CollectionAttempt.js)
const COLLECTION_PENDING_REASONS = [
  { key: 'not_home', label: 'ઘરે ન હતા' },
  { key: 'shop_closed', label: 'દુકાન બંધ હતી' },
  { key: 'asked_to_come_later', label: 'પછી આવવાનું કહ્યું' },
  { key: 'refused_to_pay', label: 'ચુકવણી કરવાની ના પાડી' },
  { key: 'out_of_station', label: 'બહારગામ ગયા છે' },
  { key: 'partial_amount', label: 'રકમ અધૂરી છે' },
  { key: 'other', label: 'અન્ય કારણ' },
];

// Cable-only subset of Complaint.category — no_internet/slow_internet/red_light_router/cable_cut/router_issue
// are broadband issues and must never surface in this app (see agent app's cable-only rule).
const COMPLAINT_CATEGORIES = [
  { key: 'e61_no_signal', label: 'E61 - No Signal' },
  { key: 'e51_channel_error', label: 'E51/999 Channel Error' },
  { key: 'channel_stuck', label: 'ચેનલ ચોટે છે' },
  { key: 'colors_only', label: 'Colors Only' },
  { key: 'relocation_transfer', label: 'Relocation/Transfer' },
  { key: 'billing_dispute', label: 'Billing Dispute' },
  { key: 'plan_change', label: 'Plan Change Request' },
  { key: 'device_issue', label: 'Device / STB Issue' },
  { key: 'installation_request', label: 'Installation Request' },
  { key: 'other', label: 'Other' },
];

function waLink(mobile, text) {
  const num = '91' + String(mobile || '').replace(/\D/g, '').slice(-10);
  return `https://wa.me/${num}?text=${encodeURIComponent(text)}`;
}

// "YYYY-MM-DDTHH:mm" in local wall-clock time, what <input type="datetime-local"> needs.
function toDatetimeLocal(d) {
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

// Client-side mirror of the backend's placeholder fill (utils/messageTemplate.js) — only used
// for the wa.me fallback text when the WhatsApp gateway send fails; the real send fills
// placeholders server-side so the customer never sees a raw {{token}}.
function fillPlaceholders(body, customer, dueDate, context = {}) {
  const map = {
    name: customer?.name || 'Customer',
    area: customer?.address?.area || '',
    customerId: customer?.customerId || '',
    mobile: customer?.mobile || '',
    expiry: dueDate || '',
    amount: fmt(customer?.outstandingBalance || 0),
    ...context,
  };
  return String(body || '').replace(/\{\{(\w+)\}\}/g, (_, key) => map[key] ?? '');
}

export default function AgentCustomerDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { setHeader } = useOutletContext();
  const { user } = useAuth();
  const [customer, setCustomer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [qrOpen, setQrOpen] = useState(false);
  const [moreMenuOpen, setMoreMenuOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [linkModal, setLinkModal] = useState(null);
  const [sendingMsg, setSendingMsg] = useState(false);
  const [msgOpen, setMsgOpen] = useState(false);
  const [msgText, setMsgText] = useState('');
  const [cpOpen, setCpOpen] = useState(false);
  const [cpReasons, setCpReasons] = useState(COLLECTION_PENDING_REASONS);
  const [cpReason, setCpReason] = useState(COLLECTION_PENDING_REASONS[0].key);
  const [cpNotes, setCpNotes] = useState('');
  const [cpNow, setCpNow] = useState(new Date());
  const [cpSaving, setCpSaving] = useState(false);
  const [hwOpen, setHwOpen] = useState(false);
  const [adjOpen, setAdjOpen] = useState(false);
  const [adjForm, setAdjForm] = useState({ type: 'reduce', amount: '', reason: '' });
  const [adjSaving, setAdjSaving] = useState(false);
  const [cpTemplates, setCpTemplates] = useState([]);
  const [cpTemplateId, setCpTemplateId] = useState('');
  const [cpSendWa, setCpSendWa] = useState(true);
  const [expiryEdit, setExpiryEdit] = useState(false);
  const [expiryVal, setExpiryVal] = useState('');
  const [expirySaving, setExpirySaving] = useState(false);
  const [complOpen, setComplOpen] = useState(false);
  const [complCategory, setComplCategory] = useState(COMPLAINT_CATEGORIES[0].key);
  const [complNote, setComplNote] = useState('');
  const [complSendWa, setComplSendWa] = useState(true);
  const [complSaving, setComplSaving] = useState(false);
  const [complImages, setComplImages] = useState([]); // [{file, preview, isVideo}] — up to 5, matches backend's complaintMediaUpload limit
  const [complAssignedTo, setComplAssignedTo] = useState('');
  const [complStaff, setComplStaff] = useState([]);

  // Deliberately not userAPI.getAll() — a plain agent login can't reach users.js's routes
  // (see complaintAPI.getAssignableStaff's own comment). Fetch once when the sheet opens
  // rather than on every render.
  useEffect(() => {
    if (complOpen) complaintAPI.getAssignableStaff().then(({ data }) => setComplStaff(data.data)).catch(() => {});
  }, [complOpen]);

  const addComplImages = (e) => {
    const files = Array.from(e.target.files || []).slice(0, 5 - complImages.length);
    if (!files.length) return;
    setComplImages(list => [...list, ...files.map(file => ({ file, preview: URL.createObjectURL(file), isVideo: file.type.startsWith('video/') }))]);
    e.target.value = '';
  };
  const removeComplImage = (i) => setComplImages(list => list.filter((_, idx) => idx !== i));
  const [retracking, setRetracking] = useState(false);
  const [retrackResult, setRetrackResult] = useState(null);

  // Default template pick: the first WhatsApp-capable one tagged "payment".
  const defaultPendingTemplateId = useCallback((templates) => {
    const tpl = templates.find((t) => t.category === 'payment' && ['whatsapp', 'both'].includes(t.channel));
    return tpl?._id || '';
  }, []);

  useEffect(() => {
    agentAppAPI.getCollectionPendingReasons().then(({ data }) => {
      if (data?.data?.length) setCpReasons(data.data);
    }).catch(() => {});
    agentAppAPI.getMessageTemplates().then(({ data }) => {
      const list = data?.data || [];
      setCpTemplates(list);
      setCpTemplateId(defaultPendingTemplateId(list));
    }).catch(() => {});
  }, [defaultPendingTemplateId]);

  const isAdmin = user?.role === 'admin';
  // Field-agent capabilities are governed by `user.fieldRights` (set per-agent from the
  // admin's Agent Detail page) — NOT the deprecated flat `user.permissions` object, which
  // predates that system and nothing writes to anymore. Reading `permissions` here silently
  // hid Edit/Collect/Adjust actions for every agent configured through the current system
  // (their fieldRights said yes, this stale object still said no) even though the backend
  // (middleware/auth.js's hasModuleRight, for FIELD_AGENT_MODULE_KEYS) already checked the
  // right field all along.
  const fieldRights = user?.fieldRights || {};
  const perms = {
    editGeneralDetail:  !!fieldRights.agentEditGeneralDetail?.edit,
    editBillingDetail:  !!fieldRights.agentEditBillingDetail?.edit,
    editCustomerStatus: !!fieldRights.agentEditCustomerStatus?.edit,
    canAdjustBalance:   !!fieldRights.agentAdjustBalance?.edit,
    canCollectPayment:  !!fieldRights.agentCollectPayment?.add,
    // No field-agent capability exists for deleting a customer (agentApp.js has no DELETE
    // /customers/:id route at all) — this stays off; the button below already just points
    // agents back to the admin panel rather than performing a delete.
    deleteCustomers: false,
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await agentAppAPI.getCustomer(id);
      setCustomer(data.data);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to load customer');
    } finally {
      setLoading(false);
    }
  }, [id]);

  // location.key changes on every navigation entry, including browser/header "back" — without
  // it, returning here after collecting a payment (same :id, no remount) would keep showing the
  // stale customer object fetched before the collection, e.g. a Collection Pending banner that
  // should have cleared.
  useEffect(() => { load(); }, [load, location.key]);

  useEffect(() => {
    if (!customer) return;
    setHeader({
      title: customer.name, showBack: true,
      menu: (
        <button
          onClick={() => setMoreMenuOpen(true)}
          style={{ background: 'none', border: 'none', color: '#fff', cursor: 'pointer', padding: 6 }}
        >
          <MoreVertical size={20} />
        </button>
      ),
    });
  }, [customer, setHeader]);

  // Kebab-menu actions (Edit Details, Balance Sheet, QR Code, Complaint, Adjust Amount) —
  const openEditDetails = () => {
    setEditForm({
      name: customer.name || '', mobile: customer.mobile || '', alternateMobile: customer.alternateMobile || '',
      line1: customer.address?.line1 || '', area: customer.address?.area || '', landmark: customer.address?.landmark || '',
      securityDeposit: customer.securityDeposit || 0, status: customer.status || 'active',
      durationType: customer.billing?.durationType || 'regular',
      durationDays: customer.billing?.durationDays || 30,
    });
    setEditOpen(true);
    setMoreMenuOpen(false);
  };
  const openComplaint = () => {
    setComplCategory(COMPLAINT_CATEGORIES[0].key);
    setComplNote('');
    setComplImages([]);
    setComplOpen(true);
    setMoreMenuOpen(false);
  };
  const openAdjustAmount = () => {
    setAdjForm({ type: 'reduce', amount: '', reason: '' });
    setAdjOpen(true);
    setMoreMenuOpen(false);
  };
  const openCollectionPending = () => {
    setCpReason(cpReasons[0].key); setCpNotes(''); setCpNow(new Date());
    setCpTemplateId(defaultPendingTemplateId(cpTemplates)); setCpSendWa(true); setCpOpen(true);
  };

  if (loading) return <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>Loading…</div>;
  if (!customer) return <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>Customer not found</div>;

  // The payment-gateway link for a given amount, or null when no gateway is configured for
  // this customer (GET /agent/customers/:id decides that server-side, following the same
  // routing rules the real charge will). This is the ONLY online-payment link the agent app
  // hands out — QR, Payment Link and Advance Link all render it, and there is deliberately no
  // raw upi:// fallback any more: a UPI deep link settles straight into the payee's own VPA,
  // so it never reaches the gateway and the collection is invisible to the CRM until someone
  // reconciles it by hand. No gateway configured now means no online option at all.
  // ?amt= makes the gateway page charge this figure instead of the live outstanding — reserved
  // for Advance Link, whose whole point is a forward-looking figure the live balance can't
  // express. Never use it for the ordinary Payment Link/QR: if a monthly auto-bill lands
  // between when the agent shares the link and when the customer opens it, a baked-in ?amt=
  // would still charge the old, now-stale figure. Those use `barePayLink` instead, which always
  // resolves to whatever's actually due on the day the customer opens it (mirrors the admin
  // desktop's own Payment Link share in CustomerDetailPage.js).
  const payLinkFor = (amount) => {
    if (!customer.paymentLinkGatewayEnabled || !customer.paymentLink) return null;
    const amt = Math.round(Number(amount) || 0);
    return amt > 0 ? `${customer.paymentLink}?amt=${amt}` : null;
  };
  const barePayLink = customer.paymentLinkGatewayEnabled && customer.paymentLink ? customer.paymentLink : null;

  const dueDate = customer.catv?.expiryDate ? new Date(customer.catv.expiryDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
  const messageText = `Dear ${customer.name}, your CableTV subscription is due on ${dueDate}. Outstanding balance: ₹${fmt(customer.outstandingBalance)}. Please pay to continue uninterrupted service.`;

  // Tries sending via the configured WhatsApp API first (silent, no app switch); falls back to
  // opening wa.me if the gateway isn't configured or the send fails. Takes the (possibly
  // agent-edited) text rather than always using the auto-generated default.
  // Whatever the system most recently generated for this customer — a completed payment
  // receipt or a still-unpaid bill, whichever is newer (ledger entries come back newest-first).
  // Used by both Bill Share and Receipt Share so they always point at the same document.
  const findLastBillPaymentId = async () => {
    try {
      const { data } = await agentAppAPI.getLedger(id);
      const last = data.data.entries.find((e) => e.paymentId);
      return last?.paymentId || null;
    } catch {
      toast.error('Failed to load bill history');
      return null;
    }
  };

  // `to` overrides the recipient (e.g. Payment/Advance Link's editable number field) —
  // defaults to the customer's own registered mobile when omitted.
  const handleSendMessage = async (text, to) => {
    const recipient = to || customer.mobile;
    if (!recipient) { toast.error('Customer has no mobile number'); return; }
    setSendingMsg(true);
    try {
      await agentAppAPI.sendWhatsAppMessage(id, text, to);
      toast.success('Message sent');
    } catch (err) {
      toast.error((err.response?.data?.message || 'WhatsApp API send failed') + ' — opening WhatsApp instead');
      window.open(waLink(recipient, text), '_blank');
    } finally {
      setSendingMsg(false);
    }
  };

  const handleRetrack = async () => {
    if (!customer.catv?.setTopBoxSerial) { toast.error('No STB serial number on this customer'); return; }
    setRetracking(true);
    setRetrackResult(null);
    try {
      const { data } = await agentAppAPI.retrackStb(id);
      setRetrackResult({ message: data.data?.message || 'Done', ok: data.data?.ok ?? true, stb: data.data?.stb });
    } catch (err) {
      setRetrackResult({ message: err.response?.data?.message || err.message || 'Retrack failed', ok: false, stb: customer.catv?.setTopBoxSerial });
    } finally {
      setRetracking(false);
    }
  };

  return (
    <div style={{ padding: 8 }}>
      {customer.pendingReason && (
        <div style={{
          background: '#fef2f2', border: '1px solid #fecaca', color: '#dc2626', borderRadius: 10,
          padding: '6px 10px', marginBottom: 8, fontSize: '.82rem', fontWeight: 600,
        }}>
          ⚠ Collection Pending: {customer.pendingReason.reason}
          <div style={{ fontWeight: 500, fontSize: '.72rem', marginTop: 1, color: '#991b1b' }}>
            {new Date(customer.pendingReason.recordedAt).toLocaleDateString('en-GB')}{customer.pendingReason.agent ? ` · ${customer.pendingReason.agent}` : ''}
          </div>
        </div>
      )}
      <div className="card" style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 10, padding: '8px 10px', marginBottom: 8 }}>
        <Field label="Bill Name" value={customer.name} />
        <Field label="Billing Number" value={customer.billingNumber} />
        <Field label="Customer Code" value={customer.customerId} />
        <Field label="Mobile Number" value={customer.mobile} link={customer.mobile ? `tel:${customer.mobile}` : null} />
        <Field label="Address" value={[customer.address?.line1, customer.address?.line2, customer.address?.landmark].filter(Boolean).join(', ')} />
        <Field label="Billing Type" value={
          (customer.billing?.durationType || 'regular') === 'dtd'
            ? `In Days (every ${customer.billing?.durationDays || 30} days)`
            : 'Monthly (Auto-renew)'
        } />
        <button onClick={() => setHwOpen(!hwOpen)} style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%',
          background: 'none', border: 'none', padding: '6px 0 2px', margin: 0, cursor: 'pointer',
        }}>
          <span style={{ color: 'var(--accent)', fontWeight: 700, fontSize: '.78rem' }}>Hardware Details</span>
          <ChevronDown size={16} color="var(--accent)" style={{ transform: hwOpen ? 'rotate(180deg)' : 'none', transition: 'transform .15s' }} />
        </button>
        {hwOpen && (
          <>
            <Field label="STB Serial" value={customer.catv?.setTopBoxSerial} copyable />
            <Field label="STB Model" value={customer.catv?.stbModel} />
            <Field label="Entity Code" value={customer.catv?.entityCode} />
            <Field label="Account Number" value={customer.catv?.accountNumber} />
            <Field label="Node" value={customer.catv?.node} last />
          </>
        )}
      </div>

      <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-around', rowGap: 10, background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 10, padding: '10px 4px', marginBottom: 8 }}>
        <IconAction icon={MessageCircle} label="Message" onClick={customer.mobile ? () => { setMsgText(messageText); setMsgOpen(true); } : undefined} />
        <IconAction icon={Link2} label="Payment Link" onClick={() => {
          if (!customer.outstandingBalance || customer.outstandingBalance <= 0) {
            toast('No due balance to collect', { icon: 'ℹ️' });
            return;
          }
          setLinkModal({ type: 'payment', amount: customer.outstandingBalance, inputAmount: '', number: customer.mobile || '' });
        }} />
        <IconAction icon={Smartphone} label="Advance Link" onClick={() => {
          // Advance = next month's subscription — the package rate IS that amount, so generate
          // the link straight away rather than making the agent type it in and confirm; only
          // falls back to the manual-entry step below when there's no package price to compute
          // (e.g. no package assigned).
          const nextMonthAmount = (customer.catv?.package?.price || 0)
            + (customer.catv?.additionalPackages || []).reduce((s, a) => s + (a.price || 0), 0);
          setLinkModal({
            type: 'advance', amount: nextMonthAmount, inputAmount: String(nextMonthAmount || ''),
            number: customer.mobile || '',
          });
        }} />
        <IconAction icon={Receipt} label="Receipt Share" onClick={async () => {
          const paymentId = await findLastBillPaymentId();
          if (!paymentId) {
            toast('Collect a payment first to share its receipt');
            return;
          }
          navigate(`/agent/receipt/${paymentId}`);
        }} />
        <IconAction icon={RotateCcw} label={retracking ? 'Retracking…' : 'Retrack STB'} disabled={retracking} color="#16a34a" onClick={handleRetrack} />
        {(isAdmin || perms.deleteCustomers) && (
          <IconAction icon={Trash2} label="Delete" color="#dc2626" onClick={() => toast('Delete must be done from the admin panel', { icon: '⚠️' })} />
        )}
      </div>

      <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 10, padding: '8px 10px', marginBottom: 8 }}>
        <div style={{ color: 'var(--accent)', fontWeight: 700, marginBottom: 4, fontSize: '.85rem' }}>Last Bill</div>
        <div style={{ textAlign: 'center', marginBottom: 6 }}>
          <div style={{ fontSize: '.78rem', color: 'var(--text-muted)' }}>{customer.outstandingBalance < 0 ? 'Advance' : 'Due Balance'}</div>
          <div style={{ fontSize: '1.4rem', fontWeight: 700, color: customer.outstandingBalance < 0 ? '#16a34a' : 'inherit' }}>
            ₹{fmt(Math.abs(customer.outstandingBalance))}
          </div>
          <div style={{ fontSize: '.78rem', color: 'var(--text-muted)' }}>
            Expiring On: {dueDate}
            {(isAdmin || perms.editBillingDetail) && !expiryEdit && (
              <button
                onClick={() => {
                  setExpiryVal(customer.catv?.expiryDate ? new Date(customer.catv.expiryDate).toISOString().slice(0, 10) : '');
                  setExpiryEdit(true);
                }}
                style={{ background: 'none', border: 'none', color: 'var(--accent)', fontWeight: 700, cursor: 'pointer', fontSize: '.78rem', padding: '0 4px' }}
              >
                (Change Date)
              </button>
            )}
          </div>
          {expiryEdit && (
            <div style={{ display: 'flex', gap: 6, justifyContent: 'center', alignItems: 'center', marginTop: 6 }}>
              <input type="date" className="form-control" style={{ maxWidth: 170 }}
                value={expiryVal} onChange={(e) => setExpiryVal(e.target.value)} />
              <button
                disabled={expirySaving || !expiryVal}
                onClick={async () => {
                  setExpirySaving(true);
                  try {
                    await agentAppAPI.updateCustomer(id, { catvExpiryDate: expiryVal });
                    toast.success('Expiry date updated');
                    setExpiryEdit(false);
                    load();
                  } catch (err) {
                    toast.error(err.response?.data?.message || 'Update failed');
                  } finally {
                    setExpirySaving(false);
                  }
                }}
                style={{ background: 'var(--accent)', color: '#fff', border: 'none', borderRadius: 8, padding: '6px 12px', fontWeight: 700, cursor: 'pointer' }}
              >
                {expirySaving ? 'Saving…' : 'Save'}
              </button>
              <button onClick={() => setExpiryEdit(false)}
                style={{ background: 'none', border: '1px solid var(--border)', borderRadius: 8, padding: '6px 10px', fontWeight: 600, cursor: 'pointer', color: 'var(--text-muted)' }}>
                Cancel
              </button>
            </div>
          )}
        </div>
      </div>

      {(isAdmin || perms.canCollectPayment) && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 8 }}>
          <button onClick={() => navigate(`/agent/customer/${id}/collect-balance`)}
            style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, background: '#16a34a', color: '#fff', border: 'none', borderRadius: 10, padding: '12px 4px', fontWeight: 700, fontSize: '.9rem', cursor: 'pointer' }}>
            <Wallet size={18} /> Collect Payment
          </button>
          <button onClick={openCollectionPending}
            style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, background: '#dc2626', color: '#fff', border: 'none', borderRadius: 10, padding: '12px 4px', fontWeight: 700, fontSize: '.9rem', cursor: 'pointer' }}>
            <AlertTriangle size={18} /> Collection Pending
          </button>
          <button onClick={() => navigate(`/agent/customer/${id}/collect`)}
            style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, background: 'var(--accent)', color: '#fff', border: 'none', borderRadius: 10, padding: '12px 4px', fontWeight: 700, fontSize: '.9rem', cursor: 'pointer' }}>
            <RefreshCw size={18} /> Renew
          </button>
        </div>
      )}

      {moreMenuOpen && (
        <div onClick={() => setMoreMenuOpen(false)} style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,.5)', display: 'flex',
          alignItems: 'flex-end', justifyContent: 'center', zIndex: 100,
        }}>
          <div onClick={(e) => e.stopPropagation()} style={{
            background: '#fff', borderRadius: '16px 16px 0 0', padding: 10, width: '100%', maxWidth: 480,
          }}>
            {(isAdmin || perms.editGeneralDetail || perms.editBillingDetail || perms.editCustomerStatus) && (
              <MenuRow icon={Pencil} label="Edit Details" onClick={openEditDetails} />
            )}
            <MenuRow icon={FileText} label="Balance Sheet" onClick={() => { setMoreMenuOpen(false); navigate(`/agent/customer/${id}/ledger`); }} />
            <MenuRow icon={QrCode} label="QR Code" onClick={() => { setMoreMenuOpen(false); setQrOpen(true); }} />
            <MenuRow icon={AlertCircle} label="Complaint" onClick={openComplaint} />
            {(isAdmin || perms.canAdjustBalance) && (
              <MenuRow icon={SlidersHorizontal} label="Adjust Amount" onClick={openAdjustAmount} last />
            )}
          </div>
        </div>
      )}

      {/* Adjust Amount bottom sheet */}
      {adjOpen && (
        <div onClick={() => setAdjOpen(false)} style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,.5)', display: 'flex',
          alignItems: 'flex-end', justifyContent: 'center', zIndex: 100
        }}>
          <div onClick={(e) => e.stopPropagation()} style={{
            background: '#fff', borderRadius: '16px 16px 0 0', padding: 18, width: '100%', maxWidth: 480
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <div style={{ fontWeight: 700 }}>Adjust Amount</div>
              <button onClick={() => setAdjOpen(false)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><X size={18} /></button>
            </div>
            <div style={{ fontSize: '.8rem', color: 'var(--text-muted)', marginBottom: 12 }}>
              Current due: <b>₹{fmt(customer.outstandingBalance)}</b> — adjustments are recorded in System Logs.
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: 8, marginBottom: 10 }}>
              <select className="form-control" value={adjForm.type} onChange={(e) => setAdjForm({ ...adjForm, type: e.target.value })}>
                <option value="reduce">Reduce (−)</option>
                <option value="add">Add (+)</option>
              </select>
              <input className="form-control" type="number" min="0" placeholder="Amount ₹"
                value={adjForm.amount} onChange={(e) => setAdjForm({ ...adjForm, amount: e.target.value })} />
            </div>
            <div style={{ marginBottom: 16 }}>
              <input className="form-control" placeholder="Reason (required)"
                value={adjForm.reason} onChange={(e) => setAdjForm({ ...adjForm, reason: e.target.value })} />
            </div>
            <button
              disabled={adjSaving}
              onClick={async () => {
                const amt = Number(adjForm.amount);
                if (!amt || amt <= 0) { toast.error('Enter a valid amount'); return; }
                if (!adjForm.reason.trim()) { toast.error('A reason is required'); return; }
                setAdjSaving(true);
                try {
                  const { data } = await agentAppAPI.adjustBalance(id, { amount: amt, type: adjForm.type, reason: adjForm.reason.trim() });
                  toast.success(data.message || 'Balance adjusted');
                  setAdjOpen(false);
                  load();
                } catch (err) {
                  toast.error(err.response?.data?.message || 'Adjustment failed');
                } finally {
                  setAdjSaving(false);
                }
              }}
              style={{ width: '100%', background: '#b45309', color: '#fff', border: 'none', borderRadius: 22, padding: '11px 0', fontWeight: 700, cursor: 'pointer' }}
            >
              {adjSaving ? 'Saving…' : 'Save Adjustment'}
            </button>
          </div>
        </div>
      )}

      {editOpen && (
        <div onClick={() => !saving && setEditOpen(false)} style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,.5)', display: 'flex',
          alignItems: 'flex-end', justifyContent: 'center', zIndex: 100
        }}>
          <div onClick={(e) => e.stopPropagation()} style={{
            background: '#fff', borderRadius: '16px 16px 0 0', padding: 18, width: '100%', maxWidth: 480, maxHeight: '85vh', overflowY: 'auto'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <div style={{ fontWeight: 700 }}>Edit Details</div>
              <button onClick={() => setEditOpen(false)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><X size={18} /></button>
            </div>

            {(isAdmin || perms.editGeneralDetail) && (
              <>
                <EditField label="Name" value={editForm.name} onChange={(v) => setEditForm({ ...editForm, name: v })} />
                <EditField label="Mobile" value={editForm.mobile} onChange={(v) => setEditForm({ ...editForm, mobile: v })} />
                <EditField label="Alternate Mobile" value={editForm.alternateMobile} onChange={(v) => setEditForm({ ...editForm, alternateMobile: v })} />
                <EditField label="Address Line 1" value={editForm.line1} onChange={(v) => setEditForm({ ...editForm, line1: v })} />
                <EditField label="Area" value={editForm.area} onChange={(v) => setEditForm({ ...editForm, area: v })} />
                <EditField label="Landmark" value={editForm.landmark} onChange={(v) => setEditForm({ ...editForm, landmark: v })} />
              </>
            )}
            {(isAdmin || perms.editBillingDetail) && (
              <>
                <EditField label="Security Deposit" type="number" value={editForm.securityDeposit} onChange={(v) => setEditForm({ ...editForm, securityDeposit: v })} />
                <div style={{ marginBottom: 10 }}>
                  <label style={{ fontSize: '.78rem', color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>Billing Type</label>
                  <select className="form-control" value={editForm.durationType} onChange={(e) => setEditForm({ ...editForm, durationType: e.target.value })}>
                    <option value="regular">Monthly (Auto-renew)</option>
                    <option value="dtd">In Days (DTD)</option>
                  </select>
                </div>
                {editForm.durationType === 'dtd' && (
                  <EditField label="Billing Duration (Days)" type="number" value={editForm.durationDays} onChange={(v) => setEditForm({ ...editForm, durationDays: v })} />
                )}
              </>
            )}
            {(isAdmin || perms.editCustomerStatus) && (
              <div style={{ marginBottom: 10 }}>
                <label style={{ fontSize: '.78rem', color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>Status</label>
                <select className="form-control" value={editForm.status} onChange={(e) => setEditForm({ ...editForm, status: e.target.value })}>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                  <option value="suspended">Suspended</option>
                </select>
              </div>
            )}

            <button
              disabled={saving}
              onClick={async () => {
                setSaving(true);
                try {
                  const payload = {};
                  if (isAdmin || perms.editGeneralDetail) {
                    Object.assign(payload, {
                      name: editForm.name, mobile: editForm.mobile, alternateMobile: editForm.alternateMobile,
                      address: { ...customer.address, line1: editForm.line1, area: editForm.area, landmark: editForm.landmark },
                    });
                  }
                  if (isAdmin || perms.editBillingDetail) {
                    payload.securityDeposit = Number(editForm.securityDeposit);
                    payload.billing = {
                      durationType: editForm.durationType,
                      ...(editForm.durationType === 'dtd' ? { durationDays: Number(editForm.durationDays) || 30 } : {}),
                    };
                  }
                  if (isAdmin || perms.editCustomerStatus) payload.status = editForm.status;

                  await agentAppAPI.updateCustomer(id, payload);
                  toast.success('Customer updated');
                  setEditOpen(false);
                  load();
                } catch (err) {
                  toast.error(err.response?.data?.message || 'Update failed');
                } finally {
                  setSaving(false);
                }
              }}
              style={{ width: '100%', background: 'var(--accent)', color: '#fff', border: 'none', borderRadius: 22, padding: '11px 0', fontWeight: 700, cursor: 'pointer', marginTop: 6 }}
            >
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      )}

      {msgOpen && (
        <div onClick={() => !sendingMsg && setMsgOpen(false)} style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,.5)', display: 'flex',
          alignItems: 'flex-end', justifyContent: 'center', zIndex: 100
        }}>
          <div onClick={(e) => e.stopPropagation()} style={{
            background: '#fff', borderRadius: '16px 16px 0 0', padding: 18, width: '100%', maxWidth: 480
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <div style={{ fontWeight: 700 }}>Edit Message</div>
              <button onClick={() => setMsgOpen(false)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><X size={18} /></button>
            </div>

            <div style={{ marginBottom: 16 }}>
              <textarea
                className="form-control" rows={5}
                value={msgText}
                onChange={(e) => setMsgText(e.target.value)}
                style={{ resize: 'vertical' }}
              />
            </div>

            <button
              disabled={sendingMsg || !msgText.trim()}
              onClick={async () => {
                await handleSendMessage(msgText);
                setMsgOpen(false);
              }}
              style={{ width: '100%', background: 'var(--bg-sidebar)', color: '#fff', border: 'none', borderRadius: 22, padding: '11px 0', fontWeight: 700, cursor: 'pointer' }}
            >
              {sendingMsg ? 'Sending…' : 'Send'}
            </button>
          </div>
        </div>
      )}

      {cpOpen && (
        <div onClick={() => !cpSaving && setCpOpen(false)} style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,.5)', display: 'flex',
          alignItems: 'flex-end', justifyContent: 'center', zIndex: 100
        }}>
          <div onClick={(e) => e.stopPropagation()} style={{
            background: '#fff', borderRadius: '16px 16px 0 0', padding: 18, width: '100%', maxWidth: 480
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <div style={{ fontWeight: 700 }}>Collection Pending</div>
              <button onClick={() => setCpOpen(false)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><X size={18} /></button>
            </div>

            {/* TEMPORARY (2026-08, this month only): visit date/time is editable so agents can
                backfill a visit they forgot to log same-day — server clamps it to
                [start of this month, now] regardless of what's picked here. */}
            <div style={{
              display: 'flex', alignItems: 'center', gap: 8, color: 'var(--text-muted)', fontSize: '.82rem',
              background: '#f3f4f6', borderRadius: 8, padding: '8px 10px', marginBottom: 12
            }}>
              <Clock size={14} style={{ flexShrink: 0 }} />
              <input type="datetime-local" className="form-control" style={{ fontSize: '.82rem', padding: '4px 8px' }}
                value={toDatetimeLocal(cpNow)}
                min={toDatetimeLocal(new Date(cpNow.getFullYear(), cpNow.getMonth(), 1))}
                max={toDatetimeLocal(new Date())}
                onChange={(e) => e.target.value && setCpNow(new Date(e.target.value))} />
            </div>

            <div style={{ marginBottom: 10 }}>
              <label style={{ fontSize: '.78rem', color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>Reason</label>
              <select className="form-control" value={cpReason} onChange={(e) => setCpReason(e.target.value)}>
                {cpReasons.map((r) => (
                  <option key={r.key} value={r.key}>{r.label}</option>
                ))}
              </select>
            </div>

            <div style={{ marginBottom: 16 }}>
              <label style={{ fontSize: '.78rem', color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>Note (optional)</label>
              <textarea className="form-control" rows={2} value={cpNotes} onChange={(e) => setCpNotes(e.target.value)} />
            </div>

            {/* One decision only: notify the customer on WhatsApp or not. On by default;
                turning it OFF is an admin-only action (agents must always notify). */}
            <div
              onClick={() => {
                if (!isAdmin && cpSendWa) { toast('Only admin can turn off the WhatsApp message'); return; }
                setCpSendWa((v) => !v);
              }}
              role="switch" aria-checked={cpSendWa}
              style={{
                marginBottom: 16, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10,
                border: '1px solid var(--border)', borderRadius: 10, padding: '10px 12px',
                cursor: 'pointer', userSelect: 'none',
              }}
            >
              <div>
                <div style={{ fontSize: '.88rem', fontWeight: 600 }}>Send WhatsApp message</div>
                <div style={{ fontSize: '.72rem', color: 'var(--text-muted)' }}>
                  {cpSendWa ? 'Customer will be notified about the pending collection' : 'No message will be sent'}
                  {!isAdmin ? ' · turning off needs admin' : ''}
                </div>
              </div>
              <span style={{
                width: 42, height: 24, borderRadius: 12, position: 'relative', flexShrink: 0,
                background: cpSendWa ? '#16a34a' : '#d1d5db', transition: 'background .15s',
              }}>
                <span style={{
                  position: 'absolute', top: 2, left: cpSendWa ? 20 : 2, width: 20, height: 20,
                  borderRadius: '50%', background: '#fff', transition: 'left .15s', boxShadow: '0 1px 2px rgba(0,0,0,.25)',
                }} />
              </span>
            </div>

            <button
              disabled={cpSaving}
              onClick={async () => {
                setCpSaving(true);
                try {
                  await agentAppAPI.markCollectionPending(id, { reason: cpReason, notes: cpNotes, recordedAt: cpNow.toISOString() });
                  toast.success('Collection pending recorded');
                  setCpOpen(false);

                  if (cpSendWa && cpTemplateId && customer.mobile) {
                    const reasonLabel = cpReasons.find((r) => r.key === cpReason)?.label || cpReason;
                    const context = {
                      value1: `${cpNow.toLocaleDateString('en-GB')} ${cpNow.toLocaleTimeString('en-GB')}`,
                      value2: reasonLabel,
                      value3: fmt(customer.outstandingBalance),
                    };
                    try {
                      await agentAppAPI.sendWhatsAppTemplate(id, cpTemplateId, context);
                      toast.success('WhatsApp message sent');
                    } catch (waErr) {
                      const tpl = cpTemplates.find((t) => t._id === cpTemplateId);
                      const filledText = fillPlaceholders(tpl?.body, customer, dueDate, context);
                      toast.error('Recorded, but WhatsApp send failed — opening WhatsApp instead');
                      window.open(waLink(customer.mobile, filledText), '_blank');
                    }
                  }
                } catch (err) {
                  toast.error(err.response?.data?.message || 'Failed to record');
                } finally {
                  setCpSaving(false);
                }
              }}
              style={{ width: '100%', background: '#dc2626', color: '#fff', border: 'none', borderRadius: 22, padding: '11px 0', fontWeight: 700, cursor: 'pointer' }}
            >
              {cpSaving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      )}

      {complOpen && (
        <div onClick={() => !complSaving && setComplOpen(false)} style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,.5)', display: 'flex',
          alignItems: 'flex-end', justifyContent: 'center', zIndex: 100
        }}>
          <div onClick={(e) => e.stopPropagation()} style={{
            background: '#fff', borderRadius: '16px 16px 0 0', padding: 18, width: '100%', maxWidth: 480
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <div style={{ fontWeight: 700 }}>Raise Complaint</div>
              <button onClick={() => setComplOpen(false)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><X size={18} /></button>
            </div>

            <div style={{ marginBottom: 10 }}>
              <label style={{ fontSize: '.78rem', color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>Issue Type</label>
              <select className="form-control" value={complCategory} onChange={(e) => setComplCategory(e.target.value)}>
                {COMPLAINT_CATEGORIES.map((c) => (
                  <option key={c.key} value={c.key}>{c.label}</option>
                ))}
              </select>
            </div>

            <div style={{ marginBottom: 10 }}>
              <label style={{ fontSize: '.78rem', color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>Assign To (optional)</label>
              <select className="form-control" value={complAssignedTo} onChange={(e) => setComplAssignedTo(e.target.value)}>
                <option value="">— Unassigned, staff will assign —</option>
                {complStaff.map((s) => (
                  <option key={s._id} value={s._id}>{s.name} ({s.role})</option>
                ))}
              </select>
            </div>

            <div style={{ marginBottom: 12 }}>
              <label style={{ fontSize: '.78rem', color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>Note</label>
              <textarea
                className="form-control" rows={3} placeholder="Describe the issue…"
                value={complNote} onChange={(e) => setComplNote(e.target.value)}
                style={{ resize: 'vertical' }}
              />
            </div>

            <div style={{ marginBottom: 12 }}>
              <label style={{ fontSize: '.78rem', color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>Photo / Video (optional)</label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {complImages.map((img, i) => (
                  <div key={i} style={{ position: 'relative', width: 64, height: 64 }}>
                    {img.isVideo ? (
                      <>
                        <video src={img.preview} style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 6, background: '#000' }} />
                        <Film size={16} color="#fff" style={{ position: 'absolute', bottom: 4, left: 4, filter: 'drop-shadow(0 0 2px rgba(0,0,0,.8))' }} />
                      </>
                    ) : (
                      <img src={img.preview} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 6 }} />
                    )}
                    <button type="button" onClick={() => removeComplImage(i)}
                      style={{ position: 'absolute', top: -6, right: -6, background: 'rgba(0,0,0,.6)', border: 'none', borderRadius: '50%', width: 20, height: 20, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
                      <X size={12} color="#fff" />
                    </button>
                  </div>
                ))}
                {complImages.length < 5 && (
                  <label style={{ width: 64, height: 64, border: '1px dashed var(--border)', borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: 'var(--text-muted)' }}>
                    <ImagePlus size={20} />
                    <input type="file" accept="image/*,video/*" multiple hidden onChange={addComplImages} />
                  </label>
                )}
              </div>
            </div>

            <div
              onClick={() => setComplSendWa((v) => !v)}
              role="switch" aria-checked={complSendWa}
              style={{
                marginBottom: 16, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10,
                border: '1px solid var(--border)', borderRadius: 10, padding: '10px 12px',
                cursor: 'pointer', userSelect: 'none',
              }}
            >
              <div>
                <div style={{ fontSize: '.88rem', fontWeight: 600 }}>Send WhatsApp message</div>
                <div style={{ fontSize: '.72rem', color: 'var(--text-muted)' }}>
                  {complSendWa ? 'Customer will be notified about this complaint' : 'No message will be sent'}
                </div>
              </div>
              <span style={{
                width: 42, height: 24, borderRadius: 12, position: 'relative', flexShrink: 0,
                background: complSendWa ? '#16a34a' : '#d1d5db', transition: 'background .15s',
              }}>
                <span style={{
                  position: 'absolute', top: 2, left: complSendWa ? 20 : 2, width: 20, height: 20,
                  borderRadius: '50%', background: '#fff', transition: 'left .15s', boxShadow: '0 1px 2px rgba(0,0,0,.25)',
                }} />
              </span>
            </div>

            <div style={{
              fontSize: '.75rem', color: 'var(--text-muted)', background: '#f3f4f6', borderRadius: 8,
              padding: '8px 10px', marginBottom: 16,
            }}>
              This complaint will be resolved within 24 working hours of registration. We appreciate your patience.
            </div>

            <button
              disabled={complSaving || !complNote.trim()}
              onClick={async () => {
                setComplSaving(true);
                try {
                  const categoryLabel = COMPLAINT_CATEGORIES.find((c) => c.key === complCategory)?.label || complCategory;
                  const fields = {
                    customer: id,
                    category: complCategory,
                    serviceType: 'catv',
                    title: categoryLabel,
                    description: complNote.trim(),
                    sendWhatsapp: complSendWa,
                    ...(complAssignedTo ? { assignedTo: complAssignedTo } : {}),
                  };
                  let payload = fields;
                  if (complImages.length) {
                    payload = new FormData();
                    Object.entries(fields).forEach(([k, v]) => payload.append(k, v));
                    complImages.forEach(img => payload.append('media', img.file));
                  }
                  const { data } = await complaintAPI.create(payload);
                  toast.success(`${data.message || 'Complaint raised'} — will be resolved within 24 working hours.`);
                  setComplOpen(false);
                } catch (err) {
                  toast.error(err.response?.data?.message || 'Failed to raise complaint');
                } finally {
                  setComplSaving(false);
                }
              }}
              style={{ width: '100%', background: 'var(--bg-sidebar)', color: '#fff', border: 'none', borderRadius: 22, padding: '11px 0', fontWeight: 700, cursor: 'pointer' }}
            >
              {complSaving ? 'Saving…' : 'Submit Complaint'}
            </button>
          </div>
        </div>
      )}

      {qrOpen && (() => {
        // Always the gateway link — it opens a secure page taking UPI, card and net banking,
        // and settles into whichever merchant account the routing rules pick, so the payment
        // is recorded against the customer automatically. Bare (no ?amt=) so it always charges
        // whatever's live-due when scanned, not today's snapshot.
        const payLink = barePayLink;
        return (
          <div onClick={() => setQrOpen(false)} style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,.5)', display: 'flex',
            alignItems: 'center', justifyContent: 'center', zIndex: 100
          }}>
            <div onClick={(e) => e.stopPropagation()} style={{ background: '#fff', borderRadius: 12, padding: 24, textAlign: 'center', position: 'relative' }}>
              <button onClick={() => setQrOpen(false)} style={{ position: 'absolute', top: 8, right: 8, background: 'none', border: 'none', cursor: 'pointer' }}>
                <X size={18} />
              </button>
              {payLink ? (
                <>
                  <QRCode value={payLink} size={180} />
                  <div style={{ marginTop: 10, fontWeight: 700 }}>₹{fmt(customer.outstandingBalance)}</div>
                  <div style={{ marginTop: 4, fontSize: '.7rem', color: 'var(--text-muted)', maxWidth: 190 }}>
                    Scan to pay by UPI, card or net banking
                  </div>
                </>
              ) : (
                <div style={{ color: 'var(--text-muted)', fontSize: '.85rem', padding: '20px 10px', maxWidth: 220 }}>
                  {customer.outstandingBalance > 0
                    ? 'Payment gateway is not configured yet — collect in cash or record the payment manually'
                    : 'No due balance to collect'}
                </div>
              )}
            </div>
          </div>
        );
      })()}

      {linkModal && (
        <div onClick={() => setLinkModal(null)} style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,.5)', display: 'flex',
          alignItems: 'center', justifyContent: 'center', zIndex: 100
        }}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: '#fff', borderRadius: 12, padding: 24, textAlign: 'center', position: 'relative', width: 280 }}>
            <button onClick={() => setLinkModal(null)} style={{ position: 'absolute', top: 8, right: 8, background: 'none', border: 'none', cursor: 'pointer' }}>
              <X size={18} />
            </button>
            <div style={{ fontWeight: 700, marginBottom: 14 }}>
              {linkModal.type === 'payment'
                ? 'Payment Link'
                : `Advance Link — ${new Date(new Date().getFullYear(), new Date().getMonth() + 1, 1).toLocaleDateString('en-IN', { month: 'short', year: 'numeric' })}`}
            </div>

            {linkModal.type === 'advance' && !linkModal.amount ? (
              <div>
                <input
                  autoFocus type="number" placeholder="Enter amount" className="form-control"
                  value={linkModal.inputAmount}
                  onChange={(e) => setLinkModal({ ...linkModal, inputAmount: e.target.value })}
                />
                <button
                  onClick={() => {
                    const amt = Number(linkModal.inputAmount);
                    if (!amt || amt <= 0) { toast.error('Enter a valid amount'); return; }
                    setLinkModal({ ...linkModal, amount: amt });
                  }}
                  style={{ marginTop: 12, width: '100%', background: 'var(--accent)', color: '#fff', border: 'none', borderRadius: 22, padding: '10px 0', fontWeight: 700, cursor: 'pointer' }}
                >
                  Generate Link
                </button>
              </div>
            ) : (() => {
              // Advance links are for NEXT month's subscription
              const nextMonth = new Date(new Date().getFullYear(), new Date().getMonth() + 1, 1)
                .toLocaleDateString('en-IN', { month: 'short', year: 'numeric' });
              // Advance Link's gateway link carries its own amount (?amt=), so it charges next
              // month's figure rather than the account's current due — which for a paid-up
              // customer would be nothing at all. The ordinary Payment Link stays bare so it
              // always resolves to whatever's live-due when opened, even if an auto-bill has
              // landed since the agent generated it — see payLinkFor's comment above.
              const payLink = linkModal.type === 'payment' ? barePayLink : payLinkFor(linkModal.amount);
              if (!payLink) {
                return (
                  <div style={{ color: 'var(--text-muted)', fontSize: '.85rem' }}>
                    Payment gateway is not configured yet — collect in cash or record the payment manually
                  </div>
                );
              }
              const paymentLinkTpl = pickTemplate(cpTemplates, 'payment_link')?.body;
              const advanceLinkTpl = pickTemplate(cpTemplates, 'advance_link')?.body;
              const waText = linkModal.type === 'payment'
                ? (paymentLinkTpl
                    ? fillTemplate(paymentLinkTpl, { name: customer.name, amount: fmt(linkModal.amount), link: payLink })
                    : `Dear ${customer.name}, please pay ₹${fmt(linkModal.amount)} using this secure link: ${payLink}`)
                : (advanceLinkTpl
                    ? fillTemplate(advanceLinkTpl, { name: customer.name, amount: fmt(linkModal.amount), link: payLink, period: nextMonth })
                    : `Dear ${customer.name}, please pay ₹${fmt(linkModal.amount)} advance for ${nextMonth} using this secure link: ${payLink}`);
              return (
                <>
                  <QRCode value={payLink} size={160} />
                  <div style={{ margin: '10px 0', fontWeight: 700 }}>₹{fmt(linkModal.amount)}</div>
                  <div style={{ fontSize: '.7rem', color: 'var(--text-muted)', marginBottom: 8 }}>
                    Pays by UPI, card or net banking
                  </div>
                  <div style={{
                    fontSize: '.72rem', color: 'var(--text-muted)', wordBreak: 'break-all',
                    background: '#f3f4f6', borderRadius: 6, padding: '6px 8px', marginBottom: 10, textAlign: 'left'
                  }}>
                    {payLink}
                  </div>
                  <a
                    href={payLink} target="_blank" rel="noreferrer"
                    style={{
                      display: 'block', width: '100%', boxSizing: 'border-box', background: '#fff', color: 'var(--accent)',
                      border: '1.5px solid var(--accent)', borderRadius: 22, padding: '9px 0', fontWeight: 700,
                      textDecoration: 'none', marginBottom: 10,
                    }}
                  >
                    Open Link
                  </a>
                  <label style={{ fontSize: '.72rem', color: 'var(--text-muted)', display: 'block', textAlign: 'left', marginBottom: 4 }}>
                    Send to (mobile number)
                  </label>
                  <input
                    type="tel" className="form-control" placeholder="10-digit mobile number"
                    value={linkModal.number} onChange={(e) => setLinkModal({ ...linkModal, number: e.target.value })}
                    style={{ marginBottom: 10 }}
                  />
                  <button
                    onClick={() => {
                      const digits = String(linkModal.number || '').replace(/\D/g, '').slice(-10);
                      if (digits.length !== 10) { toast.error('Enter a valid 10-digit mobile number'); return; }
                      handleSendMessage(waText, digits);
                      setLinkModal(null);
                    }}
                    disabled={sendingMsg}
                    style={{ display: 'block', width: '100%', background: '#25D366', color: '#fff', border: 'none', borderRadius: 22, padding: '10px 0', fontWeight: 700, cursor: 'pointer' }}
                  >
                    {sendingMsg ? 'Sending…' : 'Share via WhatsApp'}
                  </button>
                </>
              );
            })()}
          </div>
        </div>
      )}

      {retrackResult && (
        <div onClick={() => setRetrackResult(null)} style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,.5)', display: 'flex',
          alignItems: 'center', justifyContent: 'center', zIndex: 100
        }}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: '#fff', borderRadius: 12, padding: 20, position: 'relative', width: 280 }}>
            <button onClick={() => setRetrackResult(null)} style={{ position: 'absolute', top: 8, right: 8, background: 'none', border: 'none', cursor: 'pointer' }}>
              <X size={18} />
            </button>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
              <div style={{
                width: 34, height: 34, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                background: retrackResult.ok ? '#dcfce7' : '#fee2e2', flexShrink: 0,
              }}>
                {retrackResult.ok ? <CheckCircle size={18} color="#16a34a" /> : <AlertCircle size={18} color="#dc2626" />}
              </div>
              <div>
                <div style={{ fontWeight: 700, fontSize: '.9rem' }}>{retrackResult.ok ? 'Retrack Successful' : 'Retrack Failed'}</div>
                {retrackResult.stb && <div style={{ fontSize: '.72rem', color: 'var(--text-muted)' }}>STB: {retrackResult.stb}</div>}
              </div>
            </div>
            <div style={{
              background: retrackResult.ok ? '#f0fdf4' : '#fef2f2',
              border: `1px solid ${retrackResult.ok ? '#86efac' : '#fca5a5'}`,
              borderRadius: 8, padding: '10px 12px',
              color: retrackResult.ok ? '#166534' : '#991b1b',
              fontSize: '.82rem', lineHeight: 1.5, wordBreak: 'break-word',
            }}>
              {retrackResult.message}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, value, link, last, copyable }) {
  const handleCopy = async () => {
    if (!value) return;
    try {
      await navigator.clipboard.writeText(value);
      toast.success('Copied to clipboard');
    } catch {
      toast.error('Copy failed');
    }
  };
  return (
    <div style={{ display: 'flex', padding: '3px 0', borderBottom: last ? 'none' : '1px solid var(--border)', fontSize: '.82rem' }}>
      <div style={{ width: 120, color: 'var(--text-muted)', flexShrink: 0 }}>{label}</div>
      <div
        onClick={copyable && value ? handleCopy : undefined}
        style={{
          color: 'var(--text)', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6,
          cursor: copyable && value ? 'pointer' : 'default',
        }}
      >:&nbsp;
        {link ? <a href={link} style={{ color: 'var(--accent)' }}>{value || '—'}</a> : (value || '—')}
        {copyable && value && <Copy size={15} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />}
      </div>
    </div>
  );
}

function IconAction({ icon: Icon, label, href, onClick, disabled, color }) {
  const content = (
    <>
      <div style={{
        width: 34, height: 34, borderRadius: '50%', background: color || 'var(--accent)', color: '#fff',
        display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 2px',
        opacity: disabled ? 0.6 : 1,
      }}>
        <Icon size={17} />
      </div>
      <div style={{ fontSize: '.68rem', color: 'var(--text-muted)', fontWeight: 600, width: 62 }}>{label}</div>
    </>
  );
  if (href) {
    return <a href={href} target="_blank" rel="noreferrer" style={{ textDecoration: 'none', textAlign: 'center' }}>{content}</a>;
  }
  return <button onClick={onClick} disabled={disabled} style={{ background: 'none', border: 'none', cursor: disabled ? 'default' : 'pointer', textAlign: 'center' }}>{content}</button>;
}

function MenuRow({ icon: Icon, label, onClick, color, last }) {
  return (
    <button onClick={onClick} style={{
      width: '100%', display: 'flex', alignItems: 'center', gap: 12,
      background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left',
      padding: '13px 10px', borderBottom: last ? 'none' : '1px solid var(--border)',
      fontSize: '.9rem', fontWeight: 600, color: color || 'var(--text)',
    }}>
      <Icon size={18} color={color || 'var(--accent)'} />
      {label}
    </button>
  );
}

function EditField({ label, value, onChange, type = 'text' }) {
  return (
    <div style={{ marginBottom: 10 }}>
      <label style={{ fontSize: '.78rem', color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>{label}</label>
      <input className="form-control" type={type} value={value} onChange={(e) => onChange(e.target.value)} />
    </div>
  );
}

